import React, { MutableRefObject, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useUser } from '~/hooks/server/sync'
import { useTranslation } from 'react-i18next'
import { ApolloError, useApolloClient, useMutation } from '@apollo/client'

import Menu from '~/models/menu'
import Service from '~/models/service'
import System from '~/models/system'
import { ToolTypeModel } from '~/models/tool'
import {
  MenuListResponse,
  ServiceMenuListVars,
  SORT_MENUS,
  SystemMenuListVars,
  UPDATE_MENU_PRIORITY_SHOWN,
  UpdateMenuResponse
} from '~/api/menu'
import { extractSortParam, SortResponse, SortVars, UpdatePriorityShownVars } from '~/api/common'
import { GetMenusMode, GetSortingMenuId } from '~/modules/menu/selectors'
import {
  existsToolInMenus,
  makeToolPopSelectMenuVar,
  menuListReadQueryOptions,
  menuListWriteQueryOptions,
  updateChildMenuList,
  updatePriorityShownInMenus
} from '~/modules/menu/utils'
import { GetTool } from '~/modules/tool/selectors'
import { CompleteSortingSingleMenu, SortMenus, SortSingleMenu, UpdatePopSelectMenu } from '~/modules/menu/actions'
import { HideLoading, ShowLoading } from '~/modules/loading/actions'

import { toolIconTag } from '~/common/define/toolType/toolTypePropsAttr'
import { existsInModels, notEmptyModel } from '~/common/utils/model'
import { useLinkEditModalShowEvent } from '~/common/define/link'
import { isAccessibleModel } from '~/common/define/access'
import { showAlertModal } from '~/common/define/modal'
import { SpaceColor } from '~/common/define/color'
import { requestErrored } from '~/common/form/formError'
import { shownModels, sortModels, sortModelsWithFunction, sortWithIdArray, updateSort } from '~/common/utils/sort'
import { menuEditModalShowEvent, MenusMode, SingleMenuMode } from '~/common/define/menu'
import { TOOL_LIST_MAX, toolEditPath } from '~/common/define/tool'
import NavigationMenu from '~/components/molecules/navigation/menu/NavigationMenu'
import { NavigationMenuItemVar } from '~/components/molecules/navigation/menu/NavigationMenuItem'

interface Props {
  color: SpaceColor
  menu: Menu
  service: Service
  system: System
  searched: boolean
  menuRef?: MutableRefObject<any>
  toolNewRef?: MutableRefObject<any>
}

const SpaceNavigationToolMenu = ({color, menu, service, system, searched, menuRef, toolNewRef}: Props) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const client = useApolloClient()
  const user = useUser()
  const activeTool = useSelector(GetTool)
  const sortingMenuId = useSelector(GetSortingMenuId)
  const menusMode = useSelector(GetMenusMode)
  const mode = sortingMenuId ? sortingMenuId == menu.id ? SingleMenuMode.SORT : SingleMenuMode.DISABLE :
    menusMode == MenusMode.SORT ? SingleMenuMode.MENU_SORT : SingleMenuMode.VIEW

  const isAccessible = (menu: Menu) => menu.tool && isAccessibleModel(menu.tool, user, notEmptyModel(system) ? system.service.organization : service.organization, service)

  const sortedMenus = sortModels<Menu>(menu.childMenus)
  const childMenus = mode == SingleMenuMode.SORT ? sortedMenus :
    sortModelsWithFunction(sortedMenus, (menuItem) => isAccessible(menuItem) ? 1 : 0)

  const shownMenus = shownModels<Menu>(childMenus,  (menuItem) => isAccessible(menuItem))
  const [viewAll, setViewAll] = useState<boolean>((shownMenus.length == childMenus.length) || (activeTool && existsToolInMenus(childMenus, activeTool.id)))

  const [updatePriorityShown] = useMutation<UpdateMenuResponse, UpdatePriorityShownVars>(UPDATE_MENU_PRIORITY_SHOWN,
    {
      onCompleted(data) {
        const dataMenu = client.readQuery<MenuListResponse, ServiceMenuListVars|SystemMenuListVars>(menuListReadQueryOptions(service, system))
        const updatedMenus = updatePriorityShownInMenus(dataMenu.menus, menu.id, data.updateMenu.id, data.updateMenu.priority_shown)
        client.writeQuery(menuListWriteQueryOptions(service, system, updatedMenus))
        dispatch(HideLoading())
      },
      onError(error: ApolloError) {
        requestErrored(error, dispatch, t)
      }
    }
  )

  const [mutateSort] = useMutation<SortResponse, SortVars>(SORT_MENUS,
    {
      onCompleted(data) {
        dispatch(HideLoading())
      },
      onError(error: ApolloError) {
        requestErrored(error, dispatch, t)
      }
    }
  )

  const setShown = (id: number, priorityShown: boolean) => {
    dispatch(ShowLoading())
    updatePriorityShown({
      variables: {
        id: id,
        priority_shown: priorityShown
      }
    })
  }

  const onMenuListUpdated = useCallback((menuItemVars: NavigationMenuItemVar[]) => {
    const updatedChildMenus = updateSort<Menu>(sortWithIdArray(childMenus, menuItemVars.map((item) => item.id)))
    const sourceMenu = client.readQuery<MenuListResponse, ServiceMenuListVars|SystemMenuListVars>(menuListReadQueryOptions(service, system))
    client.writeQuery(menuListWriteQueryOptions(service, system, updateChildMenuList(sourceMenu.menus, menu.id, updatedChildMenus)))
    mutateSort({
      variables: {
        sort: extractSortParam(updatedChildMenus)
      }
    })
  }, [childMenus, service, system])

  const showLinkEdit = useLinkEditModalShowEvent(service, system)

  const items: NavigationMenuItemVar[] = childMenus.map((childMenu: Menu): NavigationMenuItemVar => {
    const tool = childMenu.tool
    const isLink = tool.type == ToolTypeModel.LINK
    return {
      id: childMenu.id,
      text: childMenu.tool.name,
      iconTag: toolIconTag(childMenu.tool.type),
      link: isLink ? tool.link.url : toolEditPath(notEmptyModel(system) ? {...tool, system} :  {...tool, service}),
      onEdit: isLink ? () => showLinkEdit(childMenu) : null,
      external: isLink,
      lock: tool.access ? tool.access.limit_active : false,
      onPin: () => setShown(childMenu.id, !childMenu.priority_shown),
      pin: childMenu.priority_shown,
      accessible: user ? isAccessible(childMenu) : false,
      active: mode == SingleMenuMode.VIEW && activeTool && activeTool.id == tool.id,
      shown: viewAll || mode == SingleMenuMode.SORT || existsInModels(shownMenus, childMenu),
    }
  })

  const onAdd = (left: number, top: number) => {
    menu.childMenus.length >= TOOL_LIST_MAX ?
      showAlertModal(t('validation.tool_max', {max: TOOL_LIST_MAX}), dispatch) :
      dispatch(UpdatePopSelectMenu(makeToolPopSelectMenuVar(menu, service, system, dispatch, t, left, top)))
  }

  useEffect(() => {
    if (!viewAll && activeTool && existsToolInMenus(childMenus, activeTool.id)) {
      setViewAll(true)
    }
  }, [activeTool])

  return (
    <NavigationMenu
      id={menu.id}
      title={menu.name}
      color={color}
      items={items}
      supportText={viewAll ? t('navigation:tool.create') : t('navigation:watch_all', {quantity: childMenus.length - shownMenus.length})}
      showSupportIcon={viewAll}
      onSort={() => {
        dispatch(SortSingleMenu(menu.id))
      }}
      onEdit={menuEditModalShowEvent(menu, () => {})}
      onSortMenus={() => dispatch(SortMenus())}
      onCompleteSort={() => dispatch(CompleteSortingSingleMenu())}
      onListUpdated={onMenuListUpdated}
      addTooltipText={t('navigation:tool.new')}
      mode={mode}
      onAdd={onAdd}
      onClickSupport={viewAll ? null : () => setViewAll(true)}
      onlyItems={searched}
      smallAdd={childMenus.length > 0}
      menuRef={menuRef}
      toolNewRef={toolNewRef}
    />
  )
}

export default SpaceNavigationToolMenu